/**
 * Eine Schaltfläche im Seitenkopf, die die Seite darunter bedient.
 *
 * ## Warum es das gibt
 *
 * Der Seitenkopf zeigt je Route feste Aktionen aus `entwurf/kopfbereich.ts` —
 * „Exportieren", „+ Neu", „Vorschau". Wo kein Ziel hinterlegt ist, meldete er
 * bis zum 18.08.2026: „Diese Aktion wird im Ausbau der Anwendung angebunden."
 * Das ist die Klasse Schaltfläche, die dieses Projekt an neun Stellen
 * abgeräumt hat: sie sieht aus wie ein Angebot und ist keines.
 *
 * Der Grund für die Lücke ist keine Nachlässigkeit, sondern eine fehlende
 * Verbindung: was „Exportieren" auf dem Cockpit bedeutet, weiß nur das
 * Cockpit — der Kopf kennt weder die Kacheln noch die geladenen Zahlen. Auf
 * dem Cockpit ist deshalb ein **zweiter** Knopf entstanden, direkt über den
 * Kacheln. Zwei Schaltflächen mit derselben Aufgabe an derselben Seite sind
 * eine zu viel, und die obere ist die, die man sucht.
 *
 * ## Wie es funktioniert
 *
 * Die Seite meldet beim Aufbau eine Handlung unter dem Namen an, unter dem
 * sie im Kopf steht. Der Kopf ruft sie, wenn er sie kennt, und meldet sonst
 * wie bisher, dass nichts hinterlegt ist.
 *
 * Bewusst **kein** globaler Zwischenspeicher: die Anmeldung hängt an der
 * Lebensdauer der Seite. Wer die Route verlässt, nimmt seine Handlung mit —
 * sonst zeigte der Kopf einer anderen Seite eine Aktion, die dorthin nicht
 * gehört, und exportierte im Zweifel Zahlen, die gar nicht mehr auf dem
 * Schirm stehen.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';

type Handlung = () => void;

interface Api {
  /** Meldet eine Handlung an; die Rückgabe meldet sie wieder ab. */
  anmelden: (name: string, handlung: Handlung) => () => void;
  /** Ruft die Handlung, wenn es eine gibt. Sonst `false`. */
  ausloesen: (name: string) => boolean;
  /** Für die Anzeige: kennt der Kopf zu diesem Namen eine Handlung? */
  bekannt: (name: string) => boolean;
  /** Wechselt, sobald sich die Menge der Handlungen ändert. */
  stand: number;
}

const Kontext = createContext<Api>({
  anmelden: () => () => {},
  ausloesen: () => false,
  bekannt: () => false,
  stand: 0,
});

export function Seitenaktionen({ children }: { children: ReactNode }) {
  const karte = useRef(new Map<string, Handlung>());
  /* Ein Zähler statt der Karte selbst im Zustand: die Karte wird über eine
     Referenz geführt, damit ein Anmelden nicht die ganze Anwendung neu
     zeichnet. Der Zähler sagt dem Kopf trotzdem, dass er nachsehen soll. */
  const [stand, setzeStand] = useState(0);

  const anmelden = useCallback((name: string, handlung: Handlung) => {
    karte.current.set(name, handlung);
    setzeStand((s) => s + 1);
    return () => {
      karte.current.delete(name);
      setzeStand((s) => s + 1);
    };
  }, []);

  const ausloesen = useCallback((name: string) => {
    const h = karte.current.get(name);
    if (!h) return false;
    h();
    return true;
  }, []);

  const bekannt = useCallback((name: string) => karte.current.has(name), []);

  const api = useMemo<Api>(
    () => ({ anmelden, ausloesen, bekannt, stand }),
    [anmelden, ausloesen, bekannt, stand],
  );

  return <Kontext.Provider value={api}>{children}</Kontext.Provider>;
}

/** Der Seitenkopf fragt hier nach. */
export function useSeitenaktionen(): Api {
  return useContext(Kontext);
}

/**
 * Meldet eine Handlung für die Dauer dieser Seite an.
 *
 * `handlung` wird über eine Referenz geführt: eine Funktion, die bei jedem
 * Zeichnen neu entsteht — und das tut sie in React fast immer —, würde sonst
 * bei jedem Zeichnen ab- und wieder angemeldet.
 */
export function useSeitenaktion(name: string, handlung: Handlung): void {
  const { anmelden } = useSeitenaktionen();
  const gehalten = useRef(handlung);
  gehalten.current = handlung;

  useEffect(
    () => anmelden(name, () => gehalten.current()),
    [anmelden, name],
  );
}
